/*
 * The app chrome: top nav, brand, and the outlet every screen renders into.
 *
 * NAVIGATION IS A CONTRACT (principle 1, build rule 10), and this component is
 * where that is either honoured or quietly broken. What it means concretely:
 *
 *   - REAL ANCHORS. react-router's NavLink renders an <a href>, so cmd-click
 *     opens a tab, middle-click opens a tab, right-click offers the native menu
 *     with "copy link address", and the browser's own focus and status-bar
 *     behaviour all work. A <div onClick={navigate}> would break every one of
 *     those and look identical in a screenshot.
 *   - NO BOTTOM TAB BAR. Ruled out by name for web: it collides with the browser's
 *     own bottom chrome on iOS Safari, and it is the single most common way a web
 *     app announces itself as a bad imitation of a native one.
 *   - THE BACK BUTTON IS UNTOUCHED. Nothing here intercepts history, and the only
 *     redirect in the app is the landing decision, which uses `replace` so it
 *     never becomes a step you have to click back THROUGH.
 *
 * PLURAL CHROME FOR PLURAL DATA (principle 9) applies to the Admin tab: it is
 * absent entirely for everyone else, rather than present-and-disabled. A disabled
 * control tells a user a capability exists that they cannot have, which is both
 * useless to them and a hint to an attacker.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router";

import { Brand } from "../components/Brand";
import type { User } from "../lib/api";
import { activeTabVisible } from "../lib/grace";
import styles from "./AppShell.module.css";

export interface AppShellProps {
  user: User | null;
  /**
   * THE TAB'S TWO FACTS (decision 546), and what replaced `activeEventCount`
   * here: that count's only time bound is the tournament's date window, so it
   * offered the tab all through the day after a weekend ended — 72 events, for
   * an account whose last fencer had finished 22 hours earlier and whose Active
   * screen the client had emptied ten minutes after that.
   *
   * `activeFencerCount` is the tracked population still on screen for a reason
   * that does not age out; `lastFencerDoneAt` is when the rest of them finished,
   * and `activeTabVisible` ages it with the same window that retires a card.
   *
   * Defaulted so the many tests that render the shell for its chrome do not each
   * have to state facts they do not care about.
   */
  activeFencerCount?: number;
  lastFencerDoneAt?: string;
  /**
   * Unread alerts (decision 400: newer than the seen watermark and not
   * superseded).
   *
   * THE ENTRY RENDERS WHETHER OR NOT THIS IS ZERO, unlike the Active tab beside
   * it, and the asymmetry is decision 401's rather than an oversight: the Active
   * tab answers "what strip, right now" and is a link to an empty screen on
   * almost every day, where a findable notifications feed is the whole point of
   * having one. What the count governs is the BADGE, which renders only when
   * non-zero — presence implies state (build rule 2), so a "0" would be chrome
   * announcing that there is nothing to announce.
   *
   * Defaulted, so the many tests that render the shell for its chrome need not
   * state a number they do not care about.
   */
  unreadAlertCount?: number;
  /** Rendered instead of the outlet, for the router's own error surface. */
  children?: ReactNode;
}

export function AppShell({
  user,
  activeFencerCount = 0,
  lastFencerDoneAt,
  unreadAlertCount = 0,
  children,
}: AppShellProps) {
  const { t } = useTranslation();

  // Temporal-major, in time order, which is §2's information architecture: the
  // axis a coach thinks along is when, not which fencer.
  //
  // ACTIVE IS CONDITIONAL, and it is the one tab that is (decision 324). It
  // answers "what strip, right now", so on the overwhelming majority of days it
  // would be a permanent link to an empty screen; Upcoming is the honest default.
  // Absent rather than disabled, for the same reason the Admin tab is: a disabled
  // control advertises a capability you cannot have.
  //
  // THE ROUTE SURVIVES THE TAB. /active stays registered and bookmarkable, so a
  // saved link still resolves — to Active's own empty state, which says in two
  // sentences what a missing tab only implies. Hiding the tab is a claim about
  // what is worth offering, not about what exists (principle 1's navigation
  // contract).
  //
  // AND IT CAN NOW ARRIVE WHILE YOU WATCH (roadmap stage 2 item 4). The wake-up
  // check re-asks the facts below when the app comes back to the foreground, so
  // the tab appears without a reload — see lib/wakeup.ts.
  //
  // AND IT LEAVES WITH THE LAST FENCER (decision 546). The condition is not a
  // count of live events, which stays true for a day after everyone has gone
  // home; it is the same grace window that retires the cards on the screen this
  // tab leads to, computed by the same module — so the tab and the last card
  // cannot disagree about whether the weekend is over.
  const live = activeTabVisible({ activeFencerCount, lastFencerDoneAt });
  const revealing = useTabReveal(live);

  const tabs = [
    ...(live ? [{ to: "/active", label: t("nav.active"), reveal: revealing }] : []),
    { to: "/upcoming", label: t("nav.upcoming"), reveal: false },
    { to: "/past", label: t("nav.past"), reveal: false },
  ];

  return (
    <div className={styles.shell}>
      {/*
        The skip link, first in the DOM and visible on focus. A keyboard user
        should not have to tab through the whole nav on every screen — and on
        Active in particular the nav is between them and the one datum they came
        for.
      */}
      <a href="#main" className={styles.skip}>
        {t("nav.skipToContent")}
      </a>

      <header className={styles.header}>
        <div className={styles.headerInner}>
          {/*
            The brand links home, which is the web convention and one more real
            anchor. `end` is not needed: "/" only ever matches exactly here
            because the landing route redirects away from it.
          */}
          {/*
            THE LOCKUP ON DESKTOP, THE MARK ON A PHONE. Both render and CSS shows
            one, which is the same idiom Brand already uses internally for its
            light/dark pair — and it is the reason this is not a matchMedia hook:
            no render pass has the wrong one on screen, and nothing reflows when a
            window is dragged across the breakpoint.

            §8 requires the mark to work at ~20px as the nav glyph, which is what
            the phone gets. Desktop has room for the name, and a wordmark is worth
            having where the app is one of thirty tabs.
          */}
          <NavLink to="/" className={styles.brandLink} aria-label={t("brand.name")}>
            <span className={styles.brandMark}>
              <Brand variant="mark" height={22} />
            </span>
            <span className={styles.brandLockup}>
              <Brand variant="lockup" height={22} />
            </span>
          </NavLink>

          {/*
            THE TEMPORAL TABS, IN TWO RENDERINGS, EXACTLY ONE OF WHICH IS
            DISPLAYED — the same idiom the brand pair above uses, and for the
            same reasons: no render pass has the wrong one on screen, nothing
            flashes when a window crosses the threshold, and `display: none`
            keeps the hidden one out of the accessibility tree so the sections
            are never announced twice.

            WHY A SECOND RENDERING EXISTS AT ALL. With the Admin tab present the
            row is Active + Upcoming + Past + Admin + bell + gear, which on a
            phone pushes past the viewport — and the side-scroll that produces
            interferes with interactions on the rest of the screen. Letting the
            bar scroll was the other candidate and is rejected below.

            IT IS NOT A SECOND LAYOUT (§12). One media query decides which
            rendering shows, and it is the SAME 40rem the header already used
            for the brand pair — no new number. The condition that it only
            applies when the bar is actually crowded is carried by `:has()` on
            the Admin tab rather than by a second breakpoint, so a reader
            without Admin keeps the full row at every width and pays nothing for
            a problem they do not have.
          */}
          <nav className={styles.nav} aria-label={t("nav.sections")}>
            <div className={styles.navRow}>
              {tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  className={({ isActive }) =>
                    [
                      styles.tab,
                      isActive ? styles.tabCurrent : "",
                      tab.reveal ? styles.tabRevealed : "",
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                  // aria-current is what actually tells a screen reader which
                  // section this is; the styling is the sighted half of the same
                  // statement. NavLink sets it to "page" itself, and this is the
                  // note that the visual state is not doing the work alone.
                >
                  {tab.label}
                </NavLink>
              ))}
            </div>

            <NavMenu tabs={tabs} />
          </nav>

          <div className={styles.headerEnd}>
            {/* Role-gated, and absent rather than disabled. */}
            {/*
              `tabAdmin` carries no styling of its own: it is the hook the
              header's `:has()` looks for to decide whether the temporal tabs
              are crowded enough to collapse. A class rather than a data
              attribute only because every other selector in this file is one.
            */}
            {user?.is_admin ? (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  [styles.tab, styles.tabAdmin, isActive ? styles.tabCurrent : ""]
                    .filter(Boolean)
                    .join(" ")
                }
              >
                {t("nav.admin")}
              </NavLink>
            ) : null}

            {/*
              THE NOTIFICATIONS ENTRY, beside the gear (decision 401, §4.1).
              A bell, drawn inline for the reason the gear is: it is a recognised
              pictogram and no font guarantees one.

              THE COUNT IS IN THE ACCESSIBLE NAME, not only in the badge. A
              screen-reader user gets "3 unread notifications" from the label
              rather than a bell followed by a stray numeral, which is direction
              §7's rule that status markers be perceivable non-visually — the same
              rule that keeps the feed's own "New" marker as a word.
            */}
            <NavLink
              to="/notifications"
              className={({ isActive }) =>
                isActive ? `${styles.iconTab} ${styles.tabCurrent}` : styles.iconTab
              }
              aria-label={
                unreadAlertCount > 0
                  ? t("nav.notificationCount", { count: unreadAlertCount })
                  : t("nav.notifications")
              }
            >
              <span className={styles.bellWrap}>
                <svg
                  className={styles.bell}
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    fill="currentColor"
                    d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22m7-5.5-1.5-1.7V10a5.5 5.5 0 0 0-4.25-5.36V4a1.25 1.25 0 0 0-2.5 0v.64A5.5 5.5 0 0 0 6.5 10v4.8L5 16.5a.75.75 0 0 0 .56 1.25h12.88A.75.75 0 0 0 19 16.5"
                  />
                </svg>
                {/*
                  aria-hidden because the anchor's own label already carries the
                  number: announcing it twice is worse than not at all.
                */}
                {unreadAlertCount > 0 ? (
                  <span className={styles.badge} aria-hidden="true">
                    {unreadAlertCount}
                  </span>
                ) : null}
              </span>
            </NavLink>

            <NavLink
              to="/settings"
              className={({ isActive }) =>
                isActive ? `${styles.iconTab} ${styles.tabCurrent}` : styles.iconTab
              }
              aria-label={t("nav.settings")}
            >
              {/*
                A gear, drawn as an inline SVG rather than a glyph: this one is a
                recognised pictogram where the disclosure "i" is a letter, and no
                font guarantees a gear. aria-hidden because the label is on the
                anchor.
              */}
              <svg
                className={styles.gear}
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M12 15.5A3.5 3.5 0 1 1 15.5 12 3.5 3.5 0 0 1 12 15.5m7.43-2.53c.04-.32.07-.64.07-.97s-.03-.66-.07-1l2.11-1.63a.5.5 0 0 0 .12-.64l-2-3.46a.5.5 0 0 0-.61-.22l-2.49 1a7.3 7.3 0 0 0-1.69-.98l-.38-2.65A.49.49 0 0 0 14 2h-4a.49.49 0 0 0-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1a.5.5 0 0 0-.61.22l-2 3.46a.5.5 0 0 0 .12.64L4.57 11c-.04.34-.07.67-.07 1s.03.65.07.97L2.46 14.6a.5.5 0 0 0-.12.64l2 3.46c.14.24.44.33.61.22l2.49-1c.52.39 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.17.11.47.02.61-.22l2-3.46a.5.5 0 0 0-.12-.64z"
                />
              </svg>
            </NavLink>
          </div>
        </div>
      </header>

      {/* tabIndex -1 so the skip link can actually move focus here. */}
      <main id="main" className={styles.main} tabIndex={-1}>
        {children ?? <Outlet />}
      </main>
    </div>
  );
}

interface NavTab {
  to: string;
  label: string;
  reveal: boolean;
}

/**
 * The temporal tabs collapsed into a disclosure, for the narrow bar that also
 * carries an Admin tab.
 *
 * PRINCIPLE 1 IS THE HARD CONSTRAINT, and it is why this is a disclosure
 * containing real anchors rather than the obvious control. A `<select>` would
 * have been half the code and would break all three of the guarantees the
 * navigation contract rests on: cmd-click and middle-click open a tab, the
 * right-click menu offers "copy link address", and the back button keeps
 * meaning what it meant. An `<option>` is not a link and cannot do any of it.
 * So: a button that discloses a panel of `NavLink`s, which are the SAME
 * anchors the wide rendering uses.
 *
 * THE LABEL IS THE CURRENT ITEM, which is what makes the collapse honest: the
 * wide bar shows where you are with a rule under the current word, and a
 * disclosure that said "Sections" would throw that away in exchange for the
 * space it saved. Matched on the pathname PREFIX so a future `/past/2026`
 * still reads "Past". Where no temporal section is current — Settings, Admin,
 * the feed — the label falls back to the nav's own name rather than naming a
 * section the reader is not in.
 *
 * THREE THINGS CLOSE IT, and the first is the one that is easy to miss: a
 * route change. Without it, following a link inside the panel leaves the panel
 * open over the screen you just navigated to. The other two are Escape and a
 * pointer outside, which are what a disclosure owes anyone who opened it by
 * accident.
 */
function NavMenu({ tabs }: { tabs: NavTab[] }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const panelID = useId();
  const wrap = useRef<HTMLDivElement>(null);

  const current = tabs.find(
    (tab) => location.pathname === tab.to || location.pathname.startsWith(`${tab.to}/`),
  );

  // A route change closes it. Keyed on the pathname rather than on the click,
  // so it also covers a back/forward press made while the panel is open.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    // `pointerdown` and not `click`: a click fires after the press, which on a
    // touch device is long enough to see the panel sit open under the finger.
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <div className={styles.navMenu} ref={wrap}>
      <button
        type="button"
        className={styles.navMenuButton}
        aria-expanded={open}
        aria-controls={panelID}
        onClick={() => setOpen((v) => !v)}
      >
        {current ? current.label : t("nav.sections")}
        {/* Drawn inline for the reason the gear and the bell are: a chevron is
            a recognised pictogram and no font guarantees one. aria-hidden
            because the button's name is the label beside it, and the state is
            already carried by aria-expanded. */}
        <svg
          className={styles.navMenuChevron}
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-hidden="true"
          focusable="false"
        >
          <path fill="none" stroke="currentColor" strokeWidth="2" d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {/*
        Rendered only when open, not merely hidden — the same call HonestState
        makes: a display:none subtree is skipped by screen readers anyway, so
        keeping it mounted would buy nothing and would put a second copy of
        every section link in the DOM.
      */}
      {open ? (
        <div id={panelID} className={styles.navMenuPanel}>
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) =>
                [styles.navMenuItem, isActive ? styles.navMenuItemCurrent : ""]
                  .filter(Boolean)
                  .join(" ")
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Whether the Active tab should animate its arrival.
 *
 * MOTION IS FUNCTIONAL ONLY (§6), and this is the case the rule is written for:
 * something genuinely changed — a tournament went live while the app sat in a
 * pocket — and the movement is what says so. A tab that materialises between
 * two frames reads as a rendering glitch; a tab that arrives reads as news.
 *
 * SO IT MUST NOT FIRE ON A COLD LOAD, which is what the ref is for. Landing on
 * the app with an event already live is not a change and gets no animation; the
 * ref is seeded with the first answer, so the very first render can never
 * satisfy the transition below. Chrome that animates itself on every page load
 * is decoration, and §6 bars it.
 *
 * A BOOLEAN RATHER THAN THE COUNT since decision 546, which is a simplification
 * and not a loss: this only ever compared it to zero, and the question it is
 * really asking — did the tab just appear — is now one the shell has an exact
 * answer for.
 *
 * The class is cleared when the tab goes rather than on `animationend`: the tab
 * is gone by then anyway, and clearing it is what lets a SECOND event later in
 * the day animate in too, instead of the class sitting latched from the morning.
 *
 * `prefers-reduced-motion` needs nothing here. The animation is CSS, and both
 * the OS setting and the in-app toggle neutralise it in the stylesheet — the
 * tab still ARRIVES under reduced motion, it simply arrives without the tween,
 * which is the same treatment the collapse chevron gets.
 */
function useTabReveal(live: boolean): boolean {
  const [revealing, setRevealing] = useState(false);
  const previouslyLive = useRef(live);

  useEffect(() => {
    const wasAbsent = !previouslyLive.current;
    previouslyLive.current = live;

    if (!live) setRevealing(false);
    else if (wasAbsent) setRevealing(true);
  }, [live]);

  return revealing;
}
